import { toaster } from "@/components/ui/toaster";
import { GeneralFilterData } from "@/pages/config/price-board/schema/price-board.schema";
import { formatCurrency } from "@/utils/format-currency";
import dayjs from "dayjs";
import { RefObject } from "react";
import { FieldValues } from "react-hook-form";

export interface CanvasData {
  fechaPizarra: string;
  sojaPizarra: string | number;
  maizPizarra: string | number;
  trigoPizarra: string | number;
  fecha: string;
  sojaMercado: string | number;
  maizMercado: string | number;
  trigoMercado: string | number;
  sojaUnion: string | number;
  maizUnion: string | number;
  trigoUnion: string | number;
  sojaInsumos: string | number;
  maizInsumos: string | number;
  trigoInsumos: string | number;
}

export const loadImage = (src: string) => {
  //Carga las imágenes desde donde estén
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Error cargando la imagen ${src}`));
  });
};

export const getDataForImage = (
  imageIndex: number,
  prices: FieldValues,
): Partial<CanvasData> | null => {
  // Función para obtener los datos según el índice de la imagen

  switch (imageIndex) {
    case 0: // PizarraDia.jpg
      return {
        fecha: prices?.[4] ?? "", // fecha gral
        sojaPizarra: prices?.[1] ?? "", // sojaPizarra
        maizPizarra: prices?.[2] ?? "", // maizPizarra
        trigoPizarra: prices?.[3] ?? "", // trigoPizarra
        fechaPizarra: prices?.[0] ?? "", // fecha pizarra
      };
    case 1: // CierreMercado1.jpg
      return {
        fecha: prices?.[4] ?? "", // fecha gral
        sojaMercado: prices?.[5] ?? "", // sojaMercado
        maizMercado: prices?.[6] ?? "", // maizMercado
        trigoMercado: prices?.[7] ?? "", // trigoMercado
        sojaUnion: prices?.[8] ?? "", // sojaUnion
        maizUnion: prices?.[9] ?? "", // maizUnion
        trigoUnion: prices?.[10] ?? "", // trigoUnion
      };
    case 2: // CierreMercado2.jpg
      return {
        fecha: prices?.[4] ?? "", // fecha gral
        sojaMercado: prices?.[5] ?? "", // sojaMercado
        maizMercado: prices?.[6] ?? "", // maizMercado
        trigoMercado: prices?.[7] ?? "", // trigoMercado
        sojaUnion: prices?.[8] ?? "", // sojaUnion
        maizUnion: prices?.[9] ?? "", // maizUnion
        trigoUnion: prices?.[10] ?? "", // trigoUnion
        sojaInsumos: prices?.[11] ?? "", //sojaInsumos
        maizInsumos: prices?.[12] ?? "", //maizInsumos
        trigoInsumos: prices?.[13] ?? "", //trigoInsumos
      };
    default:
      return null;
  }
};
export const getFormattedCurrency = (value: any) => {
  //Aplica formato al los valores de cotización según el tipo de dato
  if (!value) return "";
  if (typeof value === "boolean" || typeof value === "object") return "";

  const cur = typeof value === "string" ? value?.split(",") : [];
  return typeof value === "string"
    ? cur?.[0]?.replace(" ", "")
    : `$${formatCurrency(value, false)}`;
};

export const fillImageWithText = (
  data: Partial<CanvasData> | null,
  ctx: CanvasRenderingContext2D,
  imageIndex: number,
  dateData?: GeneralFilterData,
) => {
  //Carga los textos correspondientes en el canvas
  const fecha = `${data?.fecha ? dayjs(data?.fecha).format("DD/MM/YYYY") : ""}`;

  const sojaMercado = `${getFormattedCurrency(data?.sojaMercado)}`;
  const maizMercado = `${getFormattedCurrency(data?.maizMercado)}`;
  const trigoMercado = `${getFormattedCurrency(data?.trigoMercado)}`;

  const sojaUnion = `${getFormattedCurrency(data?.sojaUnion)}`;
  const maizUnion = `${getFormattedCurrency(data?.maizUnion)}`;
  const trigoUnion = `${getFormattedCurrency(data?.trigoUnion)}`;

  const sojaInsumos = `${getFormattedCurrency(data?.sojaInsumos)}`;
  const maizInsumos = `${getFormattedCurrency(data?.maizInsumos)}`;
  const trigoInsumos = `${getFormattedCurrency(data?.trigoInsumos)}`;

  const sojaPizarra = `${getFormattedCurrency(data?.sojaPizarra)}`;
  const maizPizarra = `${getFormattedCurrency(data?.maizPizarra)}`;
  const trigoPizarra = `${getFormattedCurrency(data?.trigoPizarra)}`;
  // Posicionamiento específico según la imagen
  switch (imageIndex) {
    case 0: {
      ctx.fillText(fecha, 56, 365);
      ctx.font = `600 32px Montserrat`;
      ctx.fillText(sojaPizarra, 300, 464);
      ctx.fillText(maizPizarra, 300, 559);
      ctx.fillText(trigoPizarra, 300, 653);
      ctx.font = `600 18px Montserrat`;
      ctx.fillText(
        `${dateData?.pizarra?.[0]?.fechaCotizacion ? dayjs(dateData?.pizarra?.[0]?.fechaCotizacion).format("DD/MM/YYYY") : ""}`,
        330,
        784,
      );
      return;
    }
    case 1: {
      // CierreMercado1.jpg
      ctx.fillText(fecha, 56, 365);
      ctx.fillText(sojaMercado, 70, 563);
      ctx.fillText(maizMercado, 212, 563);
      ctx.fillText(trigoMercado, 356, 563);

      ctx.fillText(sojaUnion, 70, 650);
      ctx.fillText(maizUnion, 212, 650);
      ctx.fillText(trigoUnion, 356, 650);

      ctx.font = `600 18px Montserrat`;
      ctx.fillText(`$${dateData?.dolarDivisa?.[0]?.compra ?? ""}`, 100, 850);
      ctx.fillText(`$${dateData?.dolarDivisa?.[0]?.venta ?? ""}`, 270, 850);
      return;
    }
    case 2: {
      //CierreMercado2.jpg
      ctx.fillText(fecha, 56, 365);
      ctx.fillText(sojaMercado, 70, 563);
      ctx.fillText(maizMercado, 212, 563);
      ctx.fillText(trigoMercado, 356, 563);

      ctx.fillText(sojaUnion, 70, 650);
      ctx.fillText(maizUnion, 212, 650);
      ctx.fillText(trigoUnion, 356, 650);

      ctx.fillText(sojaInsumos, 70, 738);
      ctx.fillText(maizInsumos, 212, 738);
      ctx.fillText(trigoInsumos, 356, 738);

      ctx.font = `600 18px Montserrat`;
      ctx.fillText(`$${dateData?.dolarDivisa?.[0]?.compra ?? ""}`, 100, 850);
      ctx.fillText(`$${dateData?.dolarDivisa?.[0]?.venta ?? ""}`, 270, 850);
      return;
    }
    default:
      return;
  }
};

export const drawCanvas = (
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  img: HTMLImageElement,
  imageIndex: number,
  prices: FieldValues,
  dateData?: GeneralFilterData,
) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Tamaño máximo del canvas
  const MAX_WIDTH = 540;
  const MAX_HEIGHT = 960;

  // Escala para mantener proporción
  const scale = Math.min(MAX_WIDTH / img.width, MAX_HEIGHT / img.height);

  const width = img.width * scale;
  const height = img.height * scale;

  canvas.width = width;
  canvas.height = height;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, width, height);

  // Obtener datos específicos para esta imagen
  const data = getDataForImage(imageIndex, prices);

  // Texto con tamaño fijo
  ctx.font = `600 24px Montserrat`;
  ctx.fillStyle = "#DFBD89";
  ctx.textAlign = "left";

  fillImageWithText(data, ctx, imageIndex, dateData);
};

export const handleCopyHandler = async (
  index: number,
  canvasRefs: RefObject<HTMLCanvasElement | null>[],
) => {
  const canvas = canvasRefs[index].current;
  if (!canvas) return;

  canvas.toBlob(async (blob) => {
    if (!blob) return;

    try {
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      toaster.create({
        title: "Imagen copiada al portapapeles",
        type: "success",
      });
    } catch (err) {
      console.error("Error copiando la imagen:", err);
      toaster.create({ title: "No se pudo copiar la imagen", type: "error" });
    }
  });
};

export const handleDownload = (
  index: number,
  canvasRefs: RefObject<HTMLCanvasElement | null>[],
) => {
  const fileNames = [
    "pizarra_dia",
    "cierre_mercado",
    "cierre_mercado_e_insumos",
  ];
  const canvas = canvasRefs[index].current;
  if (!canvas) return;

  const link = document.createElement("a");
  link.download = `flyer_${fileNames[index]}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
};
